import { useState } from 'react';
import { motion } from 'framer-motion';
import { Calculator, MapPin, Navigation, Info, CheckCircle } from 'lucide-react';

const FareCalculator = () => {
  const [formData, setFormData] = useState({
    pickup: '',
    drop: '',
    tripType: 'one-way',
    distance: ''
  });

  const [fare, setFare] = useState(null);
  const [showResult, setShowResult] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setShowResult(false);
  };

  const calculateFare = (e) => {
    e.preventDefault();
    
    const distance = parseFloat(formData.distance);
    if (!distance || distance <= 0) {
      alert('Please enter a valid distance');
      return;
    }

    let minFare = 0;
    let maxFare = 0;

    if (formData.tripType === 'one-way') {
      minFare = distance * 15;
      maxFare = distance * 25;
    } else if (formData.tripType === 'two-way') {
      minFare = distance * 2 * 11;
      maxFare = distance * 2 * 18;
    } else if (formData.tripType === 'per-day') {
      const days = Math.ceil(distance / 160);
      minFare = days * 2500;
      maxFare = days * 5000;
    }

    setFare({
      min: Math.round(minFare),
      max: Math.round(maxFare)
    });
    setShowResult(true);
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="fare-calculator-page">
      {/* Hero Section */}
      <section className="gradient-hero py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            transition={{ duration: 0.6 }}
            className="space-y-6"
          >
            <div className="inline-flex items-center justify-center bg-blue-100 p-4 rounded-2xl mb-4">
              <Calculator className="w-10 h-10 text-blue-600" />
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-slate-900 leading-tight tracking-tight">
              Fare Calculator
            </h1>
            <p className="text-lg md:text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
              Get an estimated fare for your journey. No hidden charges, transparent pricing.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Calculator Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-3xl shadow-2xl border border-slate-200 overflow-hidden"
          >
            {/* Form */}
            <div className="p-8 md:p-12">
              <form onSubmit={calculateFare} className="space-y-6" data-testid="fare-calculator-form">
                {/* Pickup Location */}
                <div>
                  <label htmlFor="pickup" className="block text-sm font-semibold text-slate-700 mb-2">
                    Pickup Location
                  </label>
                  <div className="relative">
                    <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="text"
                      id="pickup"
                      name="pickup"
                      data-testid="pickup-input"
                      value={formData.pickup}
                      onChange={handleChange}
                      placeholder="Enter pickup location"
                      className="w-full h-14 pl-12 pr-4 bg-slate-50 border-2 border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 rounded-xl transition-all outline-none"
                      required
                    />
                  </div>
                </div>

                {/* Drop Location */}
                <div>
                  <label htmlFor="drop" className="block text-sm font-semibold text-slate-700 mb-2">
                    Drop Location
                  </label>
                  <div className="relative">
                    <Navigation className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="text"
                      id="drop"
                      name="drop"
                      data-testid="drop-input"
                      value={formData.drop}
                      onChange={handleChange}
                      placeholder="Enter drop location"
                      className="w-full h-14 pl-12 pr-4 bg-slate-50 border-2 border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 rounded-xl transition-all outline-none"
                      required
                    />
                  </div>
                </div>

                {/* Trip Type */}
                <div>
                  <label htmlFor="tripType" className="block text-sm font-semibold text-slate-700 mb-2">
                    Trip Type
                  </label>
                  <select
                    id="tripType"
                    name="tripType"
                    data-testid="trip-type-select"
                    value={formData.tripType}
                    onChange={handleChange}
                    className="w-full h-14 px-4 bg-slate-50 border-2 border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 rounded-xl transition-all outline-none appearance-none cursor-pointer"
                    required
                  >
                    <option value="one-way">One-Way Trip (₹15-25 per km)</option>
                    <option value="two-way">Two-Way Trip (₹11-18 per km)</option>
                    <option value="per-day">Per Day Package (₹2,500-5,000 per day)</option>
                  </select>
                </div>

                {/* Distance */}
                <div>
                  <label htmlFor="distance" className="block text-sm font-semibold text-slate-700 mb-2">
                    Approximate Distance (in KM)
                  </label>
                  <input
                    type="number"
                    id="distance"
                    name="distance"
                    data-testid="distance-input"
                    value={formData.distance}
                    onChange={handleChange}
                    placeholder="Enter distance in kilometers"
                    min="1"
                    step="1"
                    className="w-full h-14 px-4 bg-slate-50 border-2 border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 rounded-xl transition-all outline-none"
                    required
                  />
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  data-testid="calculate-fare-btn"
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 rounded-full transition-all duration-300 shadow-lg hover:shadow-orange-500/30 hover:-translate-y-0.5 uppercase tracking-wide"
                >
                  Calculate Fare
                </button>
              </form>

              {/* Result */}
              {showResult && fare && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3 }}
                  data-testid="fare-result"
                  className="mt-8 p-8 bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl border-2 border-blue-200"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                    <h3 className="text-xl font-bold text-slate-900">Estimated Fare</h3>
                  </div>
                  
                  <div className="bg-white p-6 rounded-xl shadow-sm">
                    <p className="text-sm text-slate-600 mb-2">Your estimated fare range:</p>
                    <p className="text-4xl font-bold text-blue-600" data-testid="fare-amount">
                      ₹{fare.min.toLocaleString('en-IN')} - ₹{fare.max.toLocaleString('en-IN')}
                    </p>
                  </div>

                  <div className="mt-6 bg-amber-50 border-l-4 border-amber-500 p-4 rounded">
                    <div className="flex gap-3">
                      <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-amber-900">
                        <p className="font-semibold mb-2">Please Note:</p>
                        <ul className="space-y-1 text-xs">
                          <li>• This is an estimated fare based on the selected trip type</li>
                          <li>• Actual fare may vary based on car type (Sedan, SUV, Tempo Traveller)</li>
                          <li>• Toll charges, parking fees, and taxes are extra</li>
                          <li>• For accurate pricing, please call us at +91-6396867682</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 flex flex-col sm:flex-row gap-4">
                    <a
                      href="tel:+916396867682"
                      data-testid="result-call-btn"
                      className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg transition-colors duration-300 text-center"
                    >
                      Call to Book
                    </a>
                    <a
                      href="https://wa.me/916396867682"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="result-whatsapp-btn"
                      className="flex-1 bg-green-500 hover:bg-green-600 text-white font-semibold py-3 rounded-lg transition-colors duration-300 text-center"
                    >
                      WhatsApp Us
                    </a>
                  </div>
                </motion.div>
              )}
            </div>
          </motion.div>

          {/* Info Cards */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6"
          >
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
              <h4 className="font-bold text-slate-900 mb-2">One-Way Trip</h4>
              <p className="text-sm text-slate-600 mb-3">₹15 - ₹25 per km</p>
              <p className="text-xs text-slate-500">Best for single destination travel</p>
            </div>
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
              <h4 className="font-bold text-slate-900 mb-2">Two-Way Trip</h4>
              <p className="text-sm text-slate-600 mb-3">₹11 - ₹18 per km</p>
              <p className="text-xs text-slate-500">Round trip with same day return</p>
            </div>
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
              <h4 className="font-bold text-slate-900 mb-2">Per Day Package</h4>
              <p className="text-sm text-slate-600 mb-3">₹2,500 - ₹5,000</p>
              <p className="text-xs text-slate-500">Full day rental (160 KM included)</p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default FareCalculator;
